import { useEffect, useState } from 'react';
import { Alert, App, Form, Modal, Select } from 'antd';
import type { AdminStation, StationStatus } from '@goelectric/shared-types';
import { stationApi } from '../api/stationApi';
import { describeStationApiError } from '../utils/stationErrors';
import { buildUpdateStationBody, type StationEditFormState } from '../utils/stationForm';
import { STATION_STATUS_LABELS } from '../utils/format';
import { StationIdentityFields } from './StationIdentityFields';
import { StationLocationFields } from './StationLocationFields';

/** Estado del ciclo de vida editable en el PATCH (soft-delete / reactivación). */
const STATUS_OPTIONS = (['ACTIVE', 'INACTIVE'] as const).map((value) => ({
  value,
  label: STATION_STATUS_LABELS[value],
}));

interface StationFormModalProps {
  open: boolean;
  /** Cargador precargado que se edita (PATCH). */
  station: AdminStation;
  onClose: () => void;
  /** Refresca el detalle tras un guardado exitoso. */
  onSaved: () => void;
}

/**
 * Edición de Cargador (US-2.2, TG-125) en un modal de un solo formulario. Espejo de
 * `LocationFormModal`: reusa los grupos de campos del alta (`StationIdentityFields`,
 * `StationLocationFields`) para no duplicar los `Form.Item`.
 *
 * El ChargePoint ID y el protocolo se muestran **deshabilitados**: el PATCH del contrato
 * sólo acepta `vendor`, `model`, `maxPowerKw`, `status` y `locationId`; cambiar el `id` o
 * el `protocol` no re-sincronizaría el core, por eso quedaron fuera a propósito. El error
 * se muestra con el `message` real del backend (ADR 0021).
 */
export function StationFormModal({ open, station, onClose, onSaved }: StationFormModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<StationEditFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Precarga al abrir. `id`/`protocol` van deshabilitados pero con valor (el Select/Input
  // los muestra y las reglas `required` del grupo pasan).
  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    form.resetFields();
    form.setFieldsValue({
      id: station.id,
      protocol: station.protocol,
      vendor: station.vendor,
      model: station.model,
      maxPowerKw: station.maxPowerKw,
      locationId: station.locationId,
      status: station.status,
    });
  }, [open, station, form]);

  const handleFinish = async (values: StationEditFormState) => {
    setSubmitting(true);
    setError(null);
    try {
      await stationApi.update(station.id, buildUpdateStationBody(values, station.locationId));
      message.success('Cargador actualizado.');
      onSaved();
      onClose();
    } catch (caught) {
      setError(describeStationApiError(caught, 'No se pudo actualizar el cargador.').message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Editar cargador"
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={720}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo actualizar el cargador"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <StationIdentityFields mode="edit" />

        <Form.Item
          label="Estado"
          name="status"
          rules={[{ required: true, message: 'Selecciona el estado.' }]}
        >
          <Select<StationStatus> options={STATUS_OPTIONS} />
        </Form.Item>

        <StationLocationFields
          pinnedOption={{ id: station.locationId, name: station.location?.name ?? station.locationId }}
        />
      </Form>
    </Modal>
  );
}
